import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import {
  Box,
  Paper as MuiPaper,
  Tooltip,
  Typography as MuiTypography,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import {
  DataGrid,
  GridColDef,
  useGridApiRef,
  GridRowSelectionModel,
  GridLogicOperator,
} from "@mui/x-data-grid";
import { SpacingProps, spacing } from "@mui/system";

import {
  useGetContractCategoriesQuery,
  useDeleteContractCategoryMutation,
  useAddContractCategoryMutation,
} from "../../redux/slices/indexApiSlice";
import CustomNoRowsOverlay from "./CustomNoRowsOverlay";
import EnhancedDataGridToolbar from "./EnhancedDataGridToolbar";
import { t } from "i18next";
import { DialogMode } from "../../types/dialogmode";
import { IContractCategory } from "../../types/contractCategory";
import ContractCategoryDialog from "../dialogs/ContractCategoryDialog";
import useAuth from "../../hooks/useAuth";

const Paper = styled(MuiPaper)(spacing);

interface TypographyProps extends SpacingProps {
  component?: string;
}
const Typography = styled(MuiTypography)<TypographyProps>(spacing);

interface IDataGridContractCategoriesProps {
  items?: IContractCategory[];
  compactHeader?: boolean;
  onSelect?: (selected: IContractCategory[]) => void;
  selected?: string[];
  handleCardClick?: (selected: IContractCategory) => void;
  view?: "cards" | "table";
}

function DataGridContractCategories({
  ...props
}: IDataGridContractCategoriesProps) {
  const { user } = useAuth();

  const [view, setView] = useState(props.view || "cards");
  const [searchText, setSearchText] = useState<string>("");

  const [deleteCategory, { isLoading: deleting, isSuccess: isDeleted }] =
    useDeleteContractCategoryMutation();

  const {
    data: contractCategories = [],
    isLoading,
    isSuccess,
  } = useGetContractCategoriesQuery();

  const [addContractCategory] = useAddContractCategoryMutation();

  const apiRef = useGridApiRef();
  const [rowSelectionModel, setRowSelectionModel] =
    React.useState<GridRowSelectionModel>([]);

  useEffect(() => {
    if (props?.selected) {
      // Set the default selected items
      setRowSelectionModel(props.selected);
    }
  }, []);

  // const paginationModel = gridPaginationModelSelector(apiRef.current.state, apiRef.current.instanceId);

  useEffect(() => {
    // this is the id of the selected row/user
    console.log(rowSelectionModel);

    // get the kpi object from the id
    const selectedContractCategories = handleMUIDataGrid().filter(
      (p: IContractCategory) => p.id && rowSelectionModel.includes(p.id)
    );

    props?.onSelect && props?.onSelect(selectedContractCategories);
  }, [rowSelectionModel]);

  const columns: GridColDef[] = [
    // {
    //   flex: 1,
    //   field: "name",
    //   headerName: t("Name")!,
    // },
    {
      // flex: 1,
      minWidth: 250,
      field: "title",
      headerName: t("Title")!,
    },
    {
      flex: 1,
      field: "body",
      headerName: t("Description")!,
      // wrap the text
      cellClassName: "wrap",
      renderCell: (cellValues) => {
        return (
          <Tooltip title={cellValues.value}>
            <Typography
              sx={{
                whiteSpace: "wrap",
              }}
            >
              {cellValues.value}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      headerName: t("Action")!,
      width: 250,
      field: "action",
      disableColumnMenu: true,
      sortable: false,
      renderCell: (cellValues) => {
        return (
          <>
            <ContractCategoryDialog
              contractCategory={cellValues.row}
              mode={DialogMode.Edit}
              iconOnly
            />

            <ContractCategoryDialog
              contractCategory={cellValues.row}
              mode={DialogMode.Delete}
              iconOnly
            />
          </>
        );
      },
    },
  ];

  const handleImport = async (items: any) => {
    // TODO: Add a toast to notify the user that the categories have been imported

    console.log(items);

    await Promise.all(
      items.map(async (item: any) => {
        const contractCategory: IContractCategory = {
          title: item.title,
          body: item.body,
          author: user,
        };
        await addContractCategory(contractCategory);
      })
    );
  };

  // This function is triggered when the delete button in the toolbar is clicked
  const handleToolbarDeleteClick = (event: any) => {
    console.log(rowSelectionModel);

    rowSelectionModel.forEach((row) => {
      handleDeleteContractCategory({ id: row } as IContractCategory);
    });
  };

  const handleMUIDataGrid = () => {
    if (props.items) {
      return props.items;
    } else if (!isLoading && contractCategories) {
      return contractCategories;
    }

    return [];
  };

  const rows = handleMUIDataGrid();

  const handleCardClick = (sel: IContractCategory) => {
    if (sel.id && rowSelectionModel.includes(sel?.id)) {
      const newRowSelectionModel = rowSelectionModel.filter(
        (row) => row !== sel?.id
      );
      setRowSelectionModel(newRowSelectionModel);
    } else if (sel.id) {
      const newRowSelectionModel = [...rowSelectionModel, sel?.id];
      setRowSelectionModel(newRowSelectionModel);
    }
  };

  const handleDeleteContractCategory = async (
    contractCategory: IContractCategory
  ) => {
    if (contractCategory?.id) {
      await deleteCategory(contractCategory.id);

      // remove the deleted item from the rowSelectionModel
      const newRowSelectionModel = rowSelectionModel.filter(
        (row) => row !== contractCategory?.id
      );

      setRowSelectionModel(newRowSelectionModel);
    }
  };

  return (
    <Grid>
      <Paper mb={16}>
        <Paper p={4}>
          <Grid size={12}>
            <TextField // Search field
              variant="outlined"
              label={t("Search")}
              fullWidth
              value={searchText}
              onChange={(event) => {
                setSearchText(event?.target?.value);
              }}
              sx={{ mb: 2 }}
            />
          </Grid>
        </Paper>

        <Box
          // height="55vh"
          sx={{
            "& .MuiDataGrid-root": {
              border: "none",
            },
          }}
        >
          {!isLoading && isSuccess && (
            <DataGrid
              initialState={{
                pagination: { paginationModel: { page: 0, pageSize: 10 } },
              }}
              apiRef={apiRef}
              pageSizeOptions={[5, 10, 25]}
              rows={rows}
              columns={columns}
              autoHeight
              checkboxSelection
              loading={isLoading}
              onRowSelectionModelChange={(newRowSelectionModel) => {
                setRowSelectionModel(newRowSelectionModel);
              }}
              rowHeight={80}
              rowSelectionModel={rowSelectionModel}
              filterModel={{
                quickFilterLogicOperator: GridLogicOperator.And,
                quickFilterValues: searchText.split(" "),
                items: [],
              }}
              onRowDoubleClick={(row) => {
                console.log(row.row);
              }}
              slots={{
                toolbar: () => (
                  <EnhancedDataGridToolbar
                    rowSelectionModel={rowSelectionModel}
                    handleDeleteClick={handleToolbarDeleteClick}
                    view={view}
                    setView={setView}
                    title={t("Contract categories")!}
                    isLoading={isLoading}
                    deleting={deleting}
                    onImport={handleImport}
                    existingItems={contractCategories.map((item) => item.title)}
                  />
                ),
                noRowsOverlay: () => (
                  <CustomNoRowsOverlay label={t("No contract categories")} />
                ),
              }} // GridToolbar    GridToolbarQuickFilter
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
                pagination: {
                  showFirstButton: true,
                  showLastButton: true,
                },
                // pagination: {
                //   labelRowsPerPage: t("Rows per page"),
                //   labelDisplayedRows(paginationInfo) {
                //     return `${paginationInfo.from}-${paginationInfo.to} ${t("of")} ${paginationInfo.count}`;
                //   },
                // },
              }}
              sx={{
                "& .MuiDataGrid-cell": {
                  alignContent: "center",
                },
                "& .disabled": {
                  backgroundColor: (props) => props.palette.grey[100],
                  color: (props) => props.palette.grey[400],
                },
              }}
              density="comfortable"
            />
          )}
        </Box>
      </Paper>
    </Grid>
  );
}

export default DataGridContractCategories;
